import { Link, useRouterState } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { cn } from '@siliconx/shared/ui/utils';
import { api, throwIfNotOk, unwrap } from '../../lib/api';
import { qk } from '../../lib/queryKeys';
import { useAuth } from '../../lib/authContext';
import {
  JOURNEY_SEGMENTS,
  activeJourneySegment,
  deriveJourneyCounts,
  isJourneyStripRoute,
} from '../../lib/journeyStrip';

/**
 * SIL-572 (I2-C5): Discover → Negotiate → Transact strip, rendered above
 * <main> for the BUYING routes (+ /transactions) only — see
 * apps/customer/src/lib/journeyStrip.ts for the route list and the count
 * sourcing rationale. Reuses the SAME react-query keys the destination pages
 * themselves use (qk.offers() / qk.directOffers() / qk.transactions.all) so
 * navigating a segment link never triggers a redundant refetch.
 */

interface TransactionsResponse {
  data: { lifecycleState: string; buyerOrgId: string | null }[];
}

export function JourneyStrip() {
  const router = useRouterState();
  const pathname = router.location.pathname;
  const { canBuy, orgId } = useAuth();

  const shouldRender = canBuy && isJourneyStripRoute(pathname);

  const offersQ = useQuery({
    queryKey: qk.offers(),
    queryFn: async () => unwrap(await api.api.client.offers.$get()),
    enabled: shouldRender,
  });

  const directOffersQ = useQuery({
    queryKey: qk.directOffers(),
    queryFn: async () => unwrap(await api.api.client['direct-offers'].$get()),
    enabled: shouldRender,
  });

  const transactionsQ = useQuery<TransactionsResponse>({
    queryKey: qk.transactions.all,
    queryFn: async () => {
      const res = await api.api.client.transactions.$get();
      await throwIfNotOk(res);
      return (await res.json()) as unknown as TransactionsResponse;
    },
    enabled: shouldRender,
  });

  if (!shouldRender) return null;

  const { negotiate, transact } = deriveJourneyCounts({
    offers: offersQ.data?.data ?? [],
    directOffers: directOffersQ.data?.data ?? [],
    transactions: transactionsQ.data?.data ?? [],
    buyerOrgId: orgId,
  });

  const counts: Partial<Record<(typeof JOURNEY_SEGMENTS)[number]['key'], number>> = {
    negotiate,
    transact,
  };

  const active = activeJourneySegment(pathname);

  return (
    <nav
      aria-label="Buying journey"
      className="flex flex-wrap items-center gap-2 border-b border-border bg-zinc-50 px-4 pt-4 pb-3 text-sm sm:px-6 sm:pt-6 print:hidden"
    >
      {JOURNEY_SEGMENTS.map((segment, i) => {
        const count = counts[segment.key];
        const isActive = active === segment.key;
        return (
          <span key={segment.key} className="flex items-center gap-2">
            {i > 0 && (
              <span className="text-muted-foreground" aria-hidden="true">
                →
              </span>
            )}
            <Link
              to={segment.to}
              aria-current={isActive ? 'step' : undefined}
              className={cn(
                'flex items-center gap-1.5 rounded-full px-3 py-1 font-medium transition-colors',
                isActive
                  ? 'bg-brand-50 text-brand-700'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground',
              )}
            >
              {segment.label}
              {count !== undefined && (
                <span className="rounded-full bg-muted px-1.5 text-xs text-muted-foreground">
                  {count}
                </span>
              )}
            </Link>
          </span>
        );
      })}
    </nav>
  );
}
